/**
 * UI store for managing global UI state.
 * Handles connection status, loading states, modals, and notifications.
 * This store is NOT persisted.
 */

import { create } from 'zustand'
import { ConnectionStatus, PendingAction, Toast } from '@/types/ui'

interface UIStoreState {
  // State
  isLoading: boolean
  connectionStatus: ConnectionStatus
  activeModal: string | null
  pendingActions: PendingAction[]
  toasts: Toast[]

  // Actions
  setLoading: (loading: boolean) => void
  setConnectionStatus: (status: ConnectionStatus) => void
  openModal: (modalId: string) => void
  closeModal: () => void

  // Pending actions
  addPendingAction: (action: Omit<PendingAction, 'startedAt'>) => void
  removePendingAction: (id: string) => void
  isPending: (id: string) => boolean

  // Toasts
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}

export const useUIStore = create<UIStoreState>((set, get) => ({
  // Initial state
  isLoading: false,
  connectionStatus: 'disconnected',
  activeModal: null,
  pendingActions: [],
  toasts: [],

  // Loading
  setLoading: (loading) => set({ isLoading: loading }),

  // Connection
  setConnectionStatus: (status) => set({ connectionStatus: status }),

  // Modal
  openModal: (modalId) => set({ activeModal: modalId }),
  closeModal: () => set({ activeModal: null }),

  // Pending actions
  addPendingAction: (action) => {
    set((state) => ({
      pendingActions: [
        ...state.pendingActions,
        { ...action, startedAt: Date.now() },
      ],
    }))
  },

  removePendingAction: (id) => {
    set((state) => ({
      pendingActions: state.pendingActions.filter((a) => a.id !== id),
    }))
  },

  isPending: (id) => {
    return get().pendingActions.some((a) => a.id === id)
  },

  // Toasts
  addToast: (toast) => {
    // Use crypto.randomUUID if available, fallback to timestamp + random
    const id = typeof crypto !== 'undefined' && crypto.randomUUID 
      ? crypto.randomUUID() 
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }))

    // Auto-remove after duration
    if (toast.duration !== 0) {
      setTimeout(() => {
        get().removeToast(id)
      }, toast.duration || 5000)
    }
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },
}))
